import React, { type ReactNode } from 'react'
import { DecoratorNode, type NodeKey, type LexicalNode, type LexicalEditor, type SerializedLexicalNode } from 'lexical'
import type { Profile } from '../../user/Profile'

interface ProfilesContextType {
    onClick?: (userId: number) => void
    profiles: Profile[]
}

export const ProfilesContext = React.createContext<ProfilesContextType>({ profiles: [] })

export type SerializedMentionNode = SerializedLexicalNode & { type: 'mention'; userId: number }
export function isSerializedMentionNode(node: SerializedLexicalNode): node is SerializedMentionNode {
    return node.type === 'mention'
}

export class MentionNode extends DecoratorNode<ReactNode> {
    __userId: number

    static getType(): string {
        return 'mention'
    }

    getUserId() {
        return this.__userId
    }

    static clone(node: MentionNode): MentionNode {
        return new MentionNode(node.__userId, node.__key)
    }

    static importJSON(serializedNode: SerializedMentionNode) {
        return new MentionNode(serializedNode.userId)
    }

    constructor(userId: number, key?: NodeKey) {
        super(key)
        this.__userId = userId
    }

    createDOM(): HTMLElement {
        return document.createElement('span')
    }

    updateDOM(): false {
        return false
    }

    decorate(_editor: LexicalEditor): ReactNode {
        return <Mention userId={this.__userId} />
    }

    exportJSON(): SerializedMentionNode {
        return { version: 0, type: 'mention', userId: this.__userId }
    }

    isIsolated() {
        return true
    }
}

export function $createMentionNode(userId: number): MentionNode {
    return new MentionNode(userId)
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
    return node instanceof MentionNode
}

function Mention({ userId }: { userId: number }) {
    const { profiles, onClick } = React.useContext(ProfilesContext)
    const name = profiles.find((p) => p.userId === userId).name

    return (
        <span className="AppCore__lexical__MentionNode_Mention" onClick={() => onClick && onClick(userId)}>
            @{name}
        </span>
    )
}
